import React, { useState, useEffect, useMemo } from 'react';
import { Container, ListItemText, Paper, Typography, Box, Chip, Stack, List, ListItem, ListItemIcon, FormControl, InputLabel, MenuItem, OutlinedInput } from "@mui/material";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { useApplicationContext } from "ApplicationContext";
import { useDocumentTitle } from "Hooks/useDocumentTitle";
import { GetFileTypeData, GetVersionFileData } from 'apiClient/Api';
import IconPlaceholder from 'Pages/Admin/IconPlaceholder';
import { useParams } from 'react-router';
import { useTranslation } from "react-i18next";
import { MarkdownRenderer } from '../../Components/MarkdownRenderer';
import DescriptionIcon from '@mui/icons-material/Description';
import { DownloadFileButton } from 'Components/DownloadFileButton';
import {SupportIcon} from "../../SupportIcon";

type HostSystemOption = {
    id: string;
    name: string;
};

/**
 * Applications component to display a single application.
 * @returns {JSX.Element} The rendered component.
 */
export const ApplicationSingle = (): JSX.Element => {
    const { t } = useTranslation();
    const [fileTypeData, setFileTypeData] = useState<GetFileTypeData | null>(null);
    const [versionFiles, setVersionFiles] = useState<GetVersionFileData[]  | null>(null);
    const [selectedHostSystems, setSelectedHostSystems] = useState<string[]>([]);

    const [loading, setLoading] = useState<boolean>(true);
    const { application } = useParams<{ application: string }>();
    const context = useApplicationContext();
    const controller = context.api.fileType;
    useDocumentTitle(fileTypeData?.name ?? "Anwendung");

    const hostSystemOptions = useMemo<HostSystemOption[]>(() => {
        if (!versionFiles) {
            return [];
        }
        const optionsMap = new Map<string, string>();
        versionFiles.forEach((file) => {
            if (file.hostSystemId) {
                const previousValue = optionsMap.get(file.hostSystemId);
                const nextName = file.hostSystemName ?? previousValue ?? '';
                optionsMap.set(file.hostSystemId, nextName);
            }
        });
        return Array.from(optionsMap.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [versionFiles]);

    const filteredVersionFiles = useMemo(() => {
        if (!versionFiles) {
            return [];
        }
        if (selectedHostSystems.length === 0) {
            return versionFiles;
        }
        return versionFiles.filter((file) => file.hostSystemId && selectedHostSystems.includes(file.hostSystemId));
    }, [versionFiles, selectedHostSystems]);

    useEffect(() => {
        setSelectedHostSystems([]);
    }, [application]);

    useEffect(() => {
        setSelectedHostSystems((prev) => prev.filter((id) => hostSystemOptions.some((option) => option.id === id)));
    }, [hostSystemOptions]);

    /**
     * Handles updates to the host system multi-select filter.
     * @param event Select change event emitted by MUI Select component.
     */
    const handleHostSystemChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value as string[];
        setSelectedHostSystems(value);
    };

    /**
     * Removes a host system identifier from the active filter list.
     * @param hostSystemId Identifier to be removed from selection.
     */
    const handleRemoveHostSystem = (hostSystemId: string) => {
        setSelectedHostSystems((prev) => prev.filter((id) => id !== hostSystemId));
    };




    useEffect(() => {
        if (application) {
            if( context.signInState){
                controller.fileTypeGetFileType(application).then(data => {
                    if (data.data) {
                        setFileTypeData(data.data);
                    } else {
                        setFileTypeData(null);
                    }
                    setLoading(false);
                }).catch(error => {
                    context.apiError(error);
                    setLoading(false);
                });

                controller.fileTypeGetFileTypeVersions(application).then(data => {
                    if (data.data) {
                        setVersionFiles(data.data);
                    } else {
                        setVersionFiles(null);
                    }
                    setLoading(false);
                }).catch(error => {
                    context.apiError(error);
                    setLoading(false);
                });
            }
            else{
                controller.fileTypeGetFileTypePublic(application).then(data => {
                    if (data.data) {
                        setFileTypeData(data.data);
                    } else {
                        setFileTypeData(null);
                    }
                    setLoading(false);
                }).catch(error => {
                    context.apiError(error);
                    setLoading(false);
                });
                setVersionFiles(null);
            }
        }
    }, [application, context.signInState]);

    if (loading) {
        return (
            <Container component="main" maxWidth="xl" sx={{ mb: 4 }}>
                <SupportIcon />
            </Container>
        );
    }

    if (!fileTypeData) {
        return (
            <Container component="main" maxWidth="xl" sx={{ mb: 4 }}>
                <Typography variant="h5" component={"h2"}>{t('base.pages.applicationNotFound')}</Typography>
            </Container>
        );
    }

    return (
        <Container component="main" maxWidth="xl" sx={{ mb: 4 }}>
            <Paper variant="outlined" sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 } }}>
                <Box display="flex" alignItems="center" sx={{mb: 2}}>
                <IconPlaceholder size={100} iconId={fileTypeData.iconId} />
                <Box sx={{ml: 2}}>
                    <Typography variant="h4" component={"h1"}>{fileTypeData.name}</Typography>
                    <Typography variant="caption" component={"p"}>{fileTypeData.platformConfigurations?.[0]?.hostSystemName}</Typography>
                    <Stack direction="row" 
                        spacing={1}
                        useFlexGap
                        sx={{ flexWrap: 'wrap' }}
                        >
                       {fileTypeData.filterTags?.map(tag => (
                           <Chip key={tag.identifier} label={tag.name} />
                       ))}
                    </Stack>
                </Box>
                </Box>
                <MarkdownRenderer>
                    {fileTypeData.description}
                </MarkdownRenderer>
                {versionFiles != null && versionFiles.length > 0 && (
                    <>
                        <Typography variant="h5" component={"h2"}>{t('base.pages.versions')}</Typography>
                        {hostSystemOptions.length > 0 && (
                            <Box sx={{ mt: 2, mb: 2 }}>
                                <FormControl fullWidth>
                                    <InputLabel id="application-host-system-filter-label">{t('versions.filterByHostSystem')}</InputLabel>
                                    <Select
                                        labelId="application-host-system-filter-label"
                                        multiple
                                        value={selectedHostSystems}
                                        onChange={handleHostSystemChange}
                                        input={<OutlinedInput label={t('versions.filterByHostSystem')} />}
                                        renderValue={(selected) => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {selected.map((value) => {
                                                    const label = hostSystemOptions.find((option) => option.id === value)?.name ?? value;
                                                    return (
                                                        <Chip
                                                            key={value}
                                                            label={label}
                                                            onDelete={() => handleRemoveHostSystem(value)}
                                                            onMouseDown={(event) => event.stopPropagation()}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        )}
                                    >
                                        {hostSystemOptions.map((system) => (
                                            <MenuItem key={system.id} value={system.id}>
                                                {system.name || system.id}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        )}
                        <List>
                            {filteredVersionFiles.map((versionFile) => {
                                const hostSystemLabel = versionFile.hostSystemName
                                    ? t('applications.hostSystem', { host: versionFile.hostSystemName })
                                    : null;

                                return (
                                    <DownloadFileButton key={versionFile.id} id={versionFile.id!} fileName={versionFile.fileName!}>
                                        <ListItem
                                            secondaryAction={hostSystemLabel ? (
                                                <Chip
                                                    label={versionFile.hostSystemName}
                                                    size="small"
                                                    variant="outlined"
                                                    aria-label={hostSystemLabel}
                                                    title={hostSystemLabel}
                                                    onMouseDown={(event) => event.stopPropagation()}
                                                    onClick={(event) => event.stopPropagation()}
                                                />
                                            ) : undefined}
                                        >
                                            <ListItemIcon>
                                                <DescriptionIcon />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={`${versionFile.versionKey?.major}.${versionFile.versionKey?.minor}.${versionFile.versionKey?.patch}`}
                                                secondary={versionFile.fileName}
                                            />
                                        </ListItem>
                                    </DownloadFileButton>
                                );
                            })}
                        </List>
                    </>
                )}
            </Paper>
        </Container>
    );
};